/**
 * Sparkline strip above the runs table — one narrow bar per run, stacked by
 * verdict: passed, flaky, then up to `MAX_DIAGNOSIS_CATEGORIES` diagnosis categories
 * (ranked by window volume, remainder folded into "Other diagnoses"), then
 * undiagnosed failures, then everything else (skipped/degraded/canceled).
 *
 * Renders as a bare strip (no card chrome, no heading) that fuses with the
 * filter bar above and the runs table below into one bordered unit — see
 * `attached` and `TestRunsPage.tsx`'s `isTableLockup`.
 *
 * @module ui/pages/runs/test-runs/TestRunsTrendChart
 */
import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Flex,
  LoadingOrbit,
  StackedBarChart,
  Text,
  chartThemes,
  cn,
  getStatusHex,
  runBarColor,
  useTheme,
} from '@chatsdet/ui';
import { CATEGORY_LABELS } from '../../../components/issues/diagnosticDisplay';
import type { TestRunsTrendResponse } from '../../../../features/workflows/useTestRunsTrend';
import {
  buildTestRunsTrendChartData,
  padTrendRowsToSlots,
  OTHER_DIAGNOSES_KEY,
  PLACEHOLDER_KEY,
  OTHER_KEY,
  UNDIAGNOSED_KEY,
  type TestRunsTrendChartRow,
} from './testRunsTrendChartData';

/**
 * Categorical diagnosis-segment palette, validated against the `dataviz`
 * skill's `scripts/validate_palette.js` (all six checks pass in both modes;
 * `light` passes check 5 — contrast — with a WARN that's satisfied by this
 * chart always rendering a legend + tooltip, which it does below). Slot 0 is
 * assigned to the highest-volume category in the window, slot 1 the next,
 * etc. — see `buildTestRunsTrendChartData`'s ranking.
 *
 * DO NOT reorder, re-step, or otherwise "tidy" these without re-running the
 * validator: the pass/fail depends on the exact values and their order.
 */
const DIAGNOSIS_PALETTE_LIGHT = ['#14b887', '#8c47d1', '#d78c09', '#d92680'];
const DIAGNOSIS_PALETTE_DARK = ['#27a57f', '#8f54c9', '#bd7d0f', '#d03985'];

// Sparkline, not a hero: bars are short, and narrow because there are always
// TREND_SLOT_COUNT of them. BAR_SIZE only binds on very wide viewports, where
// the natural slot width would otherwise turn the strip chunky. The loading
// placeholder is sized to roughly the height the rendered chart occupies (bars
// + legend line) so the table below barely shifts when data resolves.
const BAR_HEIGHT = 40;
const BAR_SIZE = 14;
const STRIP_LOADING_HEIGHT = 62;

type TestRunsTrendChartProps = {
  className?: string;
  query: {
    data: TestRunsTrendResponse | undefined;
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
  };
  /** Fuse with the filter bar above and the table below: flush, square corners. */
  attached?: boolean;
  /** Whether any filter (target/status/triggered-by/date range) is active — see `TestRunsPage`'s `hasActiveFilters`. */
  hasActiveFilters: boolean;
  /** The trend query's window, in days — see `TestRunsPage`'s `TREND_WINDOW_DAYS`. */
  windowDays: number;
};

export function TestRunsTrendChart({
  className,
  query,
  attached = false,
  hasActiveFilters,
  windowDays,
}: TestRunsTrendChartProps) {
  const navigate = useNavigate();
  const { config } = useTheme();
  const theme = config.theme;
  const chartColors = chartThemes[theme];
  const diagnosisPalette = theme === 'cofactorDark' ? DIAGNOSIS_PALETTE_DARK : DIAGNOSIS_PALETTE_LIGHT;

  const points = query.data?.data ?? [];
  const trendWindow = query.data?.window;

  const { rows, categoryKeys, foldedCategories } = React.useMemo(
    () => buildTestRunsTrendChartData(points),
    [points]
  );

  // Always render TREND_SLOT_COUNT bars so their width — and therefore the
  // strip's rhythm — is the same at six runs as at sixty. Padding is on the
  // left, so the newest run stays pinned to the right edge.
  const paddedRows = React.useMemo(
    () => padTrendRowsToSlots(rows, categoryKeys),
    [rows, categoryKeys]
  );

  // Baseline tick drawn in the padding slots. Bottom of the stack (so it sits
  // on the axis) and deliberately absent from `legendStacks` below — an empty
  // slot is chart furniture, not a category of run.
  const placeholderStack = React.useMemo(
    () => ({ dataKey: PLACEHOLDER_KEY, name: 'No run', color: chartColors.grid }),
    [chartColors.grid]
  );

  const foldedCategoriesLabel = React.useMemo(
    () => foldedCategories.map((category) => CATEGORY_LABELS[category]).join(', '),
    [foldedCategories]
  );

  const stacks = React.useMemo(() => {
    const categoryStacks = categoryKeys.map((category, index) => ({
      dataKey: category,
      name: CATEGORY_LABELS[category],
      // categoryKeys is ranked once for the whole window (see
      // buildTestRunsTrendChartData) — a category's palette slot is therefore
      // the same in every bar, never recomputed per bar. Direct index, not
      // modulo: categoryKeys is capped at MAX_DIAGNOSIS_CATEGORIES (4) and
      // both palettes have exactly 4 entries, so this can never go out of
      // bounds — and cycling would be the wrong behavior for a categorical
      // hue assignment even if it somehow did.
      color: diagnosisPalette[index],
    }));
    const stackList = [
      { dataKey: 'passed', name: 'Passed', color: runBarColor('pass', theme) },
      { dataKey: 'flaky', name: 'Flaky', color: getStatusHex('flaky', theme) },
      ...categoryStacks,
    ];
    if (foldedCategories.length > 0) {
      stackList.push({
        dataKey: OTHER_DIAGNOSES_KEY,
        name: `Other diagnoses (${foldedCategoriesLabel})`,
        // Distinct from both `undiagnosed` (grid grey) and `other` (muted) —
        // and not one of the four diagnosis hues or a status hue.
        color: chartColors.secondary,
      });
    }
    stackList.push(
      { dataKey: UNDIAGNOSED_KEY, name: 'Undiagnosed', color: chartColors.grid },
      {
        dataKey: OTHER_KEY,
        name: 'Other',
        // The app's `skipped` status hue, not another neutral. Against the
        // grid grey used for Undiagnosed, `chartColors.muted` measures ΔE 3.3
        // (validate_palette.js, light) — far below the 15 normal-vision floor,
        // so two adjacent segments read as one grey block. `skipped` is ΔE 22.7
        // from it and is what the rest of the app already uses for these rows.
        color: getStatusHex('skipped', theme),
      }
    );
    return stackList;
  }, [
    categoryKeys,
    chartColors.grid,
    chartColors.secondary,
    diagnosisPalette,
    foldedCategories.length,
    foldedCategoriesLabel,
    theme,
  ]);

  // The placeholder tick goes first so it sits at the bottom of the stack, on
  // the axis. `stacks` (without it) is what the legend and tooltip iterate.
  const chartStacks = React.useMemo(
    () => [placeholderStack, ...stacks],
    [placeholderStack, stacks]
  );

  // Bare strip, not a Card: this fuses with the filter bar above and the
  // table below into one bordered unit when `attached` (mirrors
  // `FilterToolbar`'s own `attached` prop). When standalone it keeps its own
  // rounded border. Either way the left/right border is always present so the
  // strip reads as part of the same surface. `filter-lockup-table` is the
  // shared hook that gives every member of the lockup the same border color
  // under the Cofactor brand — without it the strip's hairline would be a
  // different shade from the toolbar and table it sits between.
  const wrapperClass = cn(
    'w-full bg-card border-x px-3 py-2',
    attached ? 'border-y filter-lockup-table' : 'rounded-md border-y',
    className
  );

  if (query.isLoading) {
    return (
      <div className={wrapperClass}>
        <Flex align="center" justify="center" style={{ height: STRIP_LOADING_HEIGHT }}>
          <LoadingOrbit size="sm" />
        </Flex>
      </div>
    );
  }

  // Error and empty are terminal states, not transitional ones — there is no
  // chart arriving to reserve space for, so they collapse to a single line
  // rather than leaving a chart-sized hole in the middle of the lockup.
  if (query.isError) {
    return (
      <div className={wrapperClass}>
        <Flex align="center" justify="center" gap="sm">
          <Text size="xs" className="text-muted-foreground">
            We couldn&apos;t load the runs trend.
          </Text>
          <Button variant="outline" size="sm" onPress={() => query.refetch()}>
            Retry
          </Button>
        </Flex>
      </div>
    );
  }

  if (rows.length === 0) {
    const emptyMessage = hasActiveFilters
      ? 'No runs matched the current filters.'
      : `No runs in the last ${windowDays} days.`;
    return (
      <div className={wrapperClass}>
        <Flex align="center" justify="center">
          <Text size="xs" className="text-muted-foreground">
            {emptyMessage}
          </Text>
        </Flex>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {trendWindow?.truncated && (
        <Text size="xs" className="text-muted-foreground mb-1">
          Showing the most recent {rows.length.toLocaleString()} of{' '}
          {trendWindow.totalItems.toLocaleString()} runs
        </Text>
      )}
      <StackedBarChart
        height={BAR_HEIGHT}
        data={paddedRows}
        xDataKey="date"
        stacks={chartStacks}
        showYAxis={false}
        // Sparkline scale: the dashed grid rules read louder than the marks
        // they sit behind, so the strip drops them entirely.
        showGrid={false}
        // Bars sit shoulder to shoulder — a sparkline reads as one shape, not
        // as a row of separated columns.
        barGap="8%"
        margin={{ top: 2, right: 2, left: 2, bottom: 0 }}
        // The default recharts legend rendered inside the chart gets cramped
        // at this height with up to 9 series (passed, flaky, up to 4 diagnosis
        // categories, other diagnoses, undiagnosed, other) — a compact
        // legend row is rendered below the chart instead. Identity must
        // still never be color-alone (the `dataviz` skill's rule for ≥2
        // series), which the row below satisfies.
        showLegend={false}
        barSize={BAR_SIZE}
        onBarClick={(data) => {
          const row = data as TestRunsTrendChartRow;
          if (row.executionId) {
            if (row.kind === 'external') {
              navigate({ to: '/runs/external/$runId', params: { runId: row.executionId } });
            } else {
              navigate({
                to: '/runs/test/$executionId',
                params: { executionId: row.executionId },
              });
            }
          }
        }}
        renderTooltip={(data) => {
          const row = data as TestRunsTrendChartRow;
          // Padding slots are inert marks, not runs with zero flows — say so
          // rather than returning null. `StackedBarChart` always wraps
          // whatever this returns in its bordered/shadowed tooltip chrome, so
          // returning null here would pop an empty box on hover; an empty
          // slot deserves an honest one-liner instead.
          if (!row.executionId) {
            return (
              <Text size="sm" className="text-muted-foreground">
                No run in this slot
              </Text>
            );
          }
          return (
            <div className="space-y-1">
              <Text size="sm" weight="medium">
                {row.date}
                <span className="ml-1.5 text-muted-foreground font-normal">
                  {row.runTargetLabel}
                </span>
              </Text>
              {stacks.map((stack) => {
                if (stack.dataKey === OTHER_DIAGNOSES_KEY) {
                  const breakdown = row.otherDiagnosesBreakdown;
                  const entries = Object.entries(breakdown).filter(([, count]) => count > 0);
                  if (entries.length === 0) return null;
                  const label = entries
                    .map(
                      ([category, count]) =>
                        `${CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}: ${count}`
                    )
                    .join(', ');
                  return (
                    <div key={stack.dataKey} className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-sm shrink-0"
                        style={{ backgroundColor: stack.color }}
                      />
                      <Text size="sm">Other diagnoses: {label}</Text>
                    </div>
                  );
                }
                const value = Number(row[stack.dataKey] ?? 0);
                if (value === 0) return null;
                return (
                  <div key={stack.dataKey} className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ backgroundColor: stack.color }}
                    />
                    <Text size="sm">
                      {stack.name}: {value}
                    </Text>
                  </div>
                );
              })}
              <Text size="xs" className="text-muted-foreground">
                Total: {row.totalWorkflows}
              </Text>
            </div>
          );
        }}
      />
      {/* Compact single-line legend — identity must never be color-alone for
          ≥2 series (dataviz skill). Wraps only if the viewport is too narrow
          to fit every series on one line. */}
      <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
        {stacks.map((stack) => (
          <Flex key={stack.dataKey} align="center" gap="xs">
            <span
              className="h-1.5 w-1.5 rounded-sm shrink-0"
              style={{ backgroundColor: stack.color }}
            />
            <Text size="xs" className="text-muted-foreground leading-none">
              {stack.name}
            </Text>
          </Flex>
        ))}
      </div>
    </div>
  );
}
